import React, { FC } from 'react';
import { Button, Card, Col, Row, Typography } from 'antd';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import Utils from 'utils/utils';
import { ClientStatus, Paths, VerificationStatus } from 'utils/constants';

import { useAppSelector, useTranslate } from 'hooks';

import { Skeleton } from 'componets/ui';

import { OverviewBlockProps } from 'models';

import { ReactComponent as IncreaseIcon } from 'assets/icons/48/increase.svg';
import { ReactComponent as GrownIcon } from 'assets/icons/grown.svg';
import { ReactComponent as AddIcon } from 'assets/icons/add-btn-icon.svg';

import { selectIsAdmin, selectOverview, selectUser } from 'selectors';

import styles from './index.module.scss';

export const Balance: FC<OverviewBlockProps> = ({ isLoading }) => {
	const overview = useAppSelector(selectOverview);
	const user = useAppSelector(selectUser);
	const isAdmin = useAppSelector(selectIsAdmin);

	const { addFundsText, accountBalanceText, thisMonthText, investText } = useTranslate([
		'addFundsText',
		'accountBalanceText',
		'thisMonthText',
		'investText',
	]);

	const isGrown = +overview.balanceGrowth >= 0;

	const isFunding =
		user.status === ClientStatus.ACTIVE &&
		user.verificationStatus === VerificationStatus.VERIFIED &&
		user.taxResidence !== null &&
		!!user.riskTolerance?.value &&
		Utils.checkYearsDifferent(user.riskTolerance?.date || '', 2);

	const isOpenCalculator =
		user.verificationStatus === VerificationStatus.VERIFIED &&
		(!user.riskTolerance?.value || !Utils.checkYearsDifferent(user.riskTolerance?.date || '', 2));

	return (
		<Card bordered={false} className={styles.root}>
			<Skeleton loading={isLoading}>
				<Row gutter={[16, 16]} justify="space-between" align="middle">
					<Col>
						<IncreaseIcon />
					</Col>
					<Col>
						<Link
							to={isFunding ? Paths.FUNDING_DEPOSIT : Utils.fundingRedirect(user)}
							state={{
								isOpenCalculator,
							}}
						>
							<Button size="small" className={styles.addFunds}>
								<AddIcon />&nbsp;
								{addFundsText}
							</Button>
						</Link>
					</Col>
					<Col span={24}>
						<Typography.Text type="secondary" className={styles.title}>
							{accountBalanceText}
						</Typography.Text>
						<Typography.Text className={styles.balance}>
							{Utils.moneyFormat(Number(overview.totalBalance))}
						</Typography.Text>
						<Typography.Text type="secondary" className={styles.increase}>
							<Typography.Text className={styles.label}>
								{Utils.moneyFormat(Number(overview.balanceGrowthAmount))}
							</Typography.Text>
							&nbsp;&nbsp;
							<Typography.Text
								type={isGrown ? 'success' : 'danger'}
								className={cn(styles.grown, { [styles.red]: !isGrown })}
							>
								<GrownIcon /> {overview.balanceGrowth}%
							</Typography.Text>
							<Typography.Text className={styles.label}>
								&nbsp;&nbsp;· {thisMonthText}
							</Typography.Text>
						</Typography.Text>

						<Button type="primary" block className={cn(styles.invest, { 'ant-btn-grey': isAdmin })}>
							<Link to={Paths.PRIMARY_MARKET}>{investText}</Link>
						</Button>
					</Col>
				</Row>
			</Skeleton>
		</Card>
	);
};
